import Page1_Content from "@/Components/Charter/Page1_Content";
import Page2_Content from "@/Components/Charter/Page2_Content";
import Page3_Content from "@/Components/Charter/Page3_Content";
import { Card } from "@/Components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/Components/ui/tabs"
import { ColumnFiltersState } from "@tanstack/react-table";
import { LayoutTemplate, Pencil, Table } from "lucide-react";
import React, { useContext } from "react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AppContext } from "../App";
import { ProjectChangeRequest } from "../Components/Charter/projectChange_table/columns";
import { Charter, CharterContext } from "../Components/Charter/Charter";
import Loading from "@/Components/loading";
import PageLockProvider, { PageName } from "@/Components/PageLockProvider";
import WizardProvider from "@/Components/wizard/wizardProvider";
import { useCharterWizardSteps } from "@/Components/Charter/wizardSteps";
import { Dialog, DialogContent } from "@/Components/ui/dialog";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";

function ProjectCharter() {
    const app = useContext(AppContext);
    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();
    const charterWizardSteps = useCharterWizardSteps();

    const locationState = useLocation().state as {
        passedColumnFilters?: (ColumnFiltersState & { workstream?: string, impactRating?: string, status?: string }) | undefined
    };
    const passedColumnFilters = locationState?.passedColumnFilters || undefined;

    const [charterApproved, setCharterApproved] = useState(false);

    const [tab, setTab] = useState("page1");
    const onTabChange = (value: string) => {
        setTab(value);
    };

    const [projectChangeTableContent, setProjectChangeTableContent] = useState<ProjectChangeRequest[]>([]);

    const [charter, setCharter] = useState<Charter>();

    const [disableApprove, setDisableApprove] = useState(false);
    const [charterLocked, setCharterLocked] = React.useState<boolean>(charterApproved);

    const [dialogOpen, setDialogOpen] = React.useState(false);

    const tabs = [
        "page1",
        "page2"
    ];

    React.useEffect(() => {
        refreshData();
    }, [app.selectedProject]);

    React.useEffect(() => {
        const openRequests = projectChangeTableContent.filter((projectChangeRequest: ProjectChangeRequest) => projectChangeRequest.status === "Open");
        openRequests.forEach((projectChangeRequest: ProjectChangeRequest) => {
            if (projectChangeRequest.reviewStatus === "Approved") {
                // if a request is open and has been approved, then the charter is not approved as those changes need to be made
                setCharterApproved(false);
                setDisableApprove(false);
            }
            if (projectChangeRequest.reviewStatus === "Pending") {
                // if a request is open and has not been approved, then the charter can not be approved as those requests need to be reviewed
                setDisableApprove(true);
                return;
            }
        })

        setCharterLocked(disableApprove || charterApproved); // if there are pending change requests, or the charter is already approved, it should be locked down
    }, [projectChangeTableContent, charterApproved, disableApprove]);

    React.useEffect(() => {
        if (passedColumnFilters) {
            setTab("page3");
        }

        const down = (e: KeyboardEvent) => {
            if (e.key === "PageUp" && (e.shiftKey)) {
                e.preventDefault()
                handleNavigateTabs(-1);
            }
            else if (e.key === "PageDown" && (e.shiftKey)) {
                e.preventDefault()
                handleNavigateTabs(1);
            }
        }
        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, []);

    const handleNavigateTabs = (val: number) => {
        setTab((prevTab) => {
            let index = tabs.indexOf(prevTab);
            index += val;
            if (index < 0) {
                index = tabs.length - 1;
            } else if (index >= tabs.length) {
                index = 0;
            }
            return tabs[index];
        });
    }

    function refreshData() {
        if (app.selectedProject) {
            setCharterApproved(false);
            setDisableApprove(false);
            setCharterLocked(false);

            document.body.style.cursor = 'wait';
            fetch("Projects/ProjectChangeRequests/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((tableData) => {
                    setProjectChangeTableContent(tableData);
                    fetch(`Projects/Charter/${app.selectedProject?.id}/`)
                        .then((res) => res.json())
                        .then((charterData) => {
                            setCharter(charterData);
                            if (charterData?.sponsorApproval) {
                                const openRequests = tableData.filter((projectChangeRequest: ProjectChangeRequest) => projectChangeRequest.status === "Open");
                                if (openRequests.length === 0) {
                                    // if the charter has been signed off, and there are no accepted change requests, then lock everything
                                    setDisableApprove(true);
                                    setCharterLocked(true);
                                    setCharterApproved(true);
                                }
                            }
                            else {
                                //setDisableApprove(!charterData?.submittedForApproval);
                            }
                        });
                })
                .finally(() => {
                    document.body.style.cursor = 'default';
                });
        }
    }

    function handleSubmitForApproval() {
        document.body.style.cursor = 'wait';
        fetch(`Projects/SubmitCharterForApproval/${app.selectedProject?.id}/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        })
            .then((res) => {
                if (res.status === 204) {
                    refreshData();
                    app.showActionNotification("Submitted","Charter submitted for approval.", "success");
                } else {
                    app.showActionNotification("Error","There was an error submitting the charter for approval.", "destructive");
                }
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function showWizardCompleteDialog() {
        if (!charter?.submittedForApproval && !charter?.sponsorApproval) {
            setDialogOpen(true);
        }
    }

    function getWizardCompleteDialog() {
        return (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="flex flex-col items-center" style={{ zoom: zoomLevel }}>
                    <div className="flex flex-col items-center">
                        <div className="font-bold">Charter Progress - 100%</div>
                        <div className={"flex flex-col min-w-[220px] h-6 m-2 rounded-full p-1 border-2 border-solid border-green-500 bg-card"}>
                            <div className="flex grow h-6 animate-growx">
                                <div className={"flex h-full rounded-full bg-green-500"} style={{ width: `100%` }} />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-center">
                        <p className="font-bold">You've successfully populated all elements of the Charter.</p>
                        <p className="font-bold mx-auto mb-4">Remember to submit your charter for approval!</p>
                    </div>
                    <button
                        className="bg-white text-card px-4 py-1 mx-auto px-12 font-bold rounded"
                        onClick={() => {
                            setDialogOpen(false);
                            handleSubmitForApproval();
                        }}
                    >
                        Submit Charter
                    </button>
                </DialogContent>
            </Dialog>
        );
    }

    const charterContextValue = {
        charterData: charter,
        setCharterData: setCharter,
        refreshCharterData: refreshData,
        charterLocked: charterLocked,
        setCharterLocked: setCharterLocked,
        submitCharterForApproval: handleSubmitForApproval,
    };

    if (charter === undefined) {
        return <Loading />
    }
    return (
        <CharterContext.Provider value={charterContextValue}>
            <Card className={`flex flex-col items-center relative ${isMobile ? "mb-8" : "p-2 m-2"}`} style={{ zoom: zoomLevel }}>
                <PageLockProvider userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} pageName={PageName.Charter} onRefreshData={refreshData}>
                    {/* <h1 className="text-3xl">Charter</h1> */}
                    <Tabs value={tab} onValueChange={onTabChange} defaultValue="page1" className="w-full p-2">
                        <TabsList className={`${isMobile ? 'w-full h-12 text-2xl' : ''}`}>
                            <TabsTrigger value="page1"><LayoutTemplate className={`${isMobile ? "h-7 w-7" : ""}`} /><span className={`${isMobile ? "text-2xl px-2" : "px-2"}`}>Charter</span></TabsTrigger>
                            <TabsTrigger value="page2"><Table className={`${isMobile ? "h-7 w-7" : ""}`} /><span className={`${isMobile ? "text-2xl px-2" : "px-2"}`}>Deliverables</span></TabsTrigger>
                            {(charter.sponsorApprovalDate || projectChangeTableContent.length > 0) && < TabsTrigger value="page3"><Pencil className={`${isMobile ? "h-7 w-7" : ""}`} /><span className={`${isMobile ? "text-2xl px-2" : "px-2"}`}>Project Change Control</span></TabsTrigger>}
                        </TabsList>
                        <TabsContent value="page1">
                            <WizardProvider wizardSteps={charterWizardSteps} autoRun={true} wizardCompleted={() => () => showWizardCompleteDialog()}>
                                <Page1_Content
                                    projectList={app.projects.filter(p => p.name !== charter?.projectName).map(p => p.name)}
                                />
                            </WizardProvider>
                        </TabsContent>
                        <TabsContent value="page2"><Page2_Content disableApprove={disableApprove} /></TabsContent>
                        <TabsContent value="page3"><Page3_Content
                            passedColumnFilters={passedColumnFilters as ColumnFiltersState & { workstream?: string, impactRating?: string, status?: string }}
                            projectChangeTableContent={projectChangeTableContent}
                            setProjectChangeTableContent={setProjectChangeTableContent}
                        /></TabsContent>
                    </Tabs>
                    {getWizardCompleteDialog()}
                </PageLockProvider>
            </ Card>
        </CharterContext.Provider>
    );
}

export default ProjectCharter;